import {
    faArrowLeft,
    faChevronLeft,
    faChevronRight,
    faSearchPlus,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { ReactImageGalleryItem } from "react-image-gallery";

import { Props } from "@/mk2/apps/wiki/containers/ArticleExperiencePhoto/ArticleExperiencePhotoPage";
import { wikiArticleExperiencePhotoUrl, wikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { ImageGalleryLoadable } from "@/mk2/components/ImageGalleryLoadable";
import { Failure } from "@/mk2/components/errors/Failure";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { url } from "@/mk2/helpers/urls";
import { ExperiencePhotoEntity } from "@/mk2/schemas";
import { BZ2_BACK } from "@/mk/autogenerated/translation";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./ArticleExperiencePhoto.mscss";
import "./ArticleExperiencePhoto.scss";

class ArticleExperiencePhoto extends React.Component<Props> {
    public render() {
        const { loadingState, article, experience } = this.props;

        return (
            <div className={styles.ArticleExperiencePhoto}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInitAndSuccess}
                    onRenderSuccess={this.renderInitAndSuccess}
                    onRenderFailure={this.renderFailure}
                    hasData={Boolean(article && experience)}
                />
            </div>
        );
    }

    private renderInit = () => {
        return null;
    };

    private renderFailure = () => {
        const {
            match: {
                params: { categorySlug, articleSlug },
            },
            errorMessage,
            staticContext,
        } = this.props;

        return (
            <Failure
                staticContext={staticContext}
                errorMessage={errorMessage}
                onRetry={this.load}
                backButton={
                    <Btn
                        type={BtnType.Blue}
                        label={BZ2_BACK}
                        icon={faArrowLeft}
                        link={url(wikiArticleUrl, { categorySlug, articleSlug })}
                    />
                }
            />
        );
    };

    private renderInitAndSuccess = () => {
        const { experience, photoId, staticContext } = this.props;

        if (!experience) {
            return this.renderInit();
        }

        const experiencePhoto: ExperiencePhotoEntity = experience.photos.find(
            (p) => p.photofile.id === photoId,
        );
        if (!experiencePhoto) {
            staticContext.statusCode = 404;
            return this.renderFailure();
        }

        const currentIndex: number = experience.photos.findIndex((p) => p === experiencePhoto);

        const galleryPhotos: ReactImageGalleryItem[] = experience.photos.map((p) => ({
            original: photoUrl(p.photofile, "s738x738"),
            thumbnail: photoUrl(p.photofile, "s150x150"),
            fullSizeUrl: photoUrl(p.photofile, "s1600x1600"),
        }));

        return (
            <>
                <div className={styles.ArticleExperiencePhoto__gallery}>
                    <ImageGalleryLoadable
                        items={galleryPhotos}
                        showIndex
                        showThumbnails={false}
                        autoPlay={false}
                        showPlayButton={false}
                        showFullscreenButton={false}
                        slideDuration={500}
                        startIndex={currentIndex}
                        onSlide={this.onSlide}
                        renderLeftNav={this.renderLeftNav}
                        renderRightNav={this.renderRightNav}
                        renderCustomControls={this.renderCustomControls}
                    />
                </div>
            </>
        );
    };

    private renderLeftNav = (onClick, disabled) => {
        return (
            <div
                key="prev"
                className={styles.ArticleExperiencePhoto__prev}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon
                    icon={faChevronLeft}
                    className={styles.ArticleExperiencePhoto__prev__icon}
                />
            </div>
        );
    };

    private renderRightNav = (onClick, disabled) => {
        return (
            <div
                key="next"
                className={styles.ArticleExperiencePhoto__next}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon
                    icon={faChevronRight}
                    className={styles.ArticleExperiencePhoto__next__icon}
                />
            </div>
        );
    };

    private renderCustomControls = () => {
        const { experience, photoId } = this.props;
        const photo: ExperiencePhotoEntity = experience.photos.find(
            (p) => p.photofile.id === photoId,
        );
        const showMagnifier = photo?.photofile?.versions?.includes("s1600x1600");
        if (!showMagnifier) {
            return null;
        }

        return (
            <div className={styles.ArticleExperiencePhoto__controls}>
                {showMagnifier && (
                    <a
                        data-google-interstitial="false"
                        key="magnifier"
                        href={photoUrl(photo.photofile, "s1600x1600")}
                        target="_blank"
                        rel="noopener"
                        className={styles.ArticleExperiencePhoto__controls__control}
                    >
                        <FontAwesomeIcon icon={faSearchPlus} />
                    </a>
                )}
            </div>
        );
    };

    private onSlide = (currentIndex: number) => {
        const {
            experience,
            history,
            match: { params },
        } = this.props;
        const photo = experience.photos[currentIndex];
        history.replace({
            pathname: url(wikiArticleExperiencePhotoUrl, {
                categorySlug: params.categorySlug,
                articleSlug: params.articleSlug,
                experienceId: params.experienceId,
                photoId: photo.photofile.id,
            }),
        });
    };

    private load = () => {
        const {
            match: {
                params: { categorySlug, articleSlug },
            },
            onLoad,
        } = this.props;
        onLoad(articleSlug, categorySlug);
    };
}

export default ArticleExperiencePhoto;
